import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import type { KnowledgeBase } from '@/types/supabase';
import { generateEmbedding } from '@/lib/embeddings';

export async function searchKnowledgeBase(kbName: string, query: string, limit = 5) {
  const { data: kbRow, error: kbError } = await supabase
    .from('knowledge_bases')
    .select('id')
    .eq('name', kbName)
    .single();

  if (kbError || !kbRow) {
    throw new Error(`Knowledge base '${kbName}' not found.`);
  }

  const queryEmbedding = await generateEmbedding(query);
  if (!queryEmbedding) {
    throw new Error('Failed to generate query embedding.');
  }

  const { data, error } = await (supabase.rpc as any)('match_knowledgebase_documents', {
    query_embedding: queryEmbedding,
    kb_id: (kbRow as { id: string }).id,
    match_threshold: 0.78,
    match_count: limit,
  });

  if (error) {
    console.error('[searchKnowledgeBase] Supabase RPC failed:', error);
    throw error;
  }

  return data;
}

export function useKnowledgeBaseCore() {
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchKnowledgeBases = useCallback(async () => {
    if (!user) {
      setKnowledgeBases([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const { data: ownedKbs, error: ownedError } = await supabase
        .from('knowledge_bases')
        .select('*')
        .eq('created_by', user.id)
        .order('created_at', { ascending: false });

      if (ownedError) throw ownedError;

      const { data: sharedRows, error: sharedError } = await supabase
        .from('knowledge_base_shares')
        .select('knowledge_bases:knowledge_base_id(*)')
        .eq('user_id', user.id);

      if (sharedError) throw sharedError;

      const sharedKbs = ((sharedRows ?? []) as Array<{ knowledge_bases: KnowledgeBase | null }>)
        .map((row) => row.knowledge_bases)
        .filter((kb): kb is KnowledgeBase => Boolean(kb));

      const allKbs = [...((ownedKbs ?? []) as KnowledgeBase[])];
      sharedKbs.forEach((sharedKb) => {
        if (!allKbs.some((kb) => kb.id === sharedKb.id)) {
          allKbs.push(sharedKb);
        }
      });

      const withCounts = await Promise.all(
        allKbs.map(async (kb) => {
          const [{ count: docCount }, { count: shareCount }] = await Promise.all([
            supabase
              .from('knowledge_base_documents')
              .select('*', { count: 'exact', head: true })
              .eq('knowledge_base_id', kb.id),
            supabase
              .from('knowledge_base_shares')
              .select('*', { count: 'exact', head: true })
              .eq('knowledge_base_id', kb.id),
          ]);

          return {
            ...kb,
            documents_count: docCount || 0,
            documents: [],
            shared_with_count: shareCount || 0,
          };
        })
      );

      setKnowledgeBases(withCounts);
    } catch (error) {
      console.error('Error fetching knowledge bases:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch knowledge bases',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast, user]);

  const getKnowledgeBase = useCallback(async (kbId: string) => {
    try {
      const { data, error } = await supabase
        .from('knowledge_bases')
        .select('*')
        .eq('id', kbId)
        .single();

      if (error) throw error;

      const [{ data: documents, error: docError }, { count: shareCount, error: shareError }] = await Promise.all([
        supabase
          .from('knowledge_base_documents')
          .select('*')
          .eq('knowledge_base_id', kbId)
          .order('created_at', { ascending: false }),
        supabase
          .from('knowledge_base_shares')
          .select('*', { count: 'exact', head: true })
          .eq('knowledge_base_id', kbId),
      ]);

      if (docError) throw docError;
      if (shareError) throw shareError;

      return {
        ...(data as KnowledgeBase),
        documents: (documents ?? []) as any,
        shared_with_count: shareCount || 0,
      };
    } catch (error) {
      console.error('Error fetching knowledge base:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch knowledge base',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  const getKnowledgeBaseByName = useCallback(async (name: string) => {
    try {
      const { data, error } = await supabase
        .from('knowledge_bases')
        .select('*')
        .ilike('name', name)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;
      const knowledgeBase = data as KnowledgeBase;

      const [{ data: documents, error: docError }, { count: shareCount, error: shareError }] = await Promise.all([
        supabase
          .from('knowledge_base_documents')
          .select('*')
          .eq('knowledge_base_id', knowledgeBase.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('knowledge_base_shares')
          .select('*', { count: 'exact', head: true })
          .eq('knowledge_base_id', knowledgeBase.id),
      ]);

      if (docError) throw docError;
      if (shareError) throw shareError;

      return {
        ...(data as KnowledgeBase),
        documents: (documents ?? []) as any,
        shared_with_count: shareCount || 0,
      };
    } catch (error) {
      console.error('[getKnowledgeBaseByName] Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch knowledge base',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  const createKnowledgeBase = useCallback(async (
    name: string,
    description = '',
    organizationId?: string | null,
    isPrivate = false
  ) => {
    if (!user) return null;

    try {
      const { data, error } = await (supabase as any)
        .from('knowledge_bases')
        .insert({
          name,
          description,
          organization_id: organizationId ?? null,
          created_by: user.id,
          is_private: isPrivate,
        })
        .select('*')
        .single();

      if (error) throw error;

      return (data as { id: string }).id;
    } catch (error: any) {
      console.error('Error creating knowledge base:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create knowledge base',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast, user]);

  const updateKnowledgeBase = useCallback(async (
    id: string,
    name: string,
    description = '',
    isPrivate?: boolean
  ) => {
    if (!user) return false;

    try {
      const updates: Record<string, unknown> = {
        name,
        description,
        updated_at: new Date().toISOString(),
      };

      if (isPrivate !== undefined) {
        updates.is_private = isPrivate;
      }

      const { error } = await (supabase as any)
        .from('knowledge_bases')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error updating knowledge base:', error);
      toast({
        title: 'Error',
        description: 'Failed to update knowledge base',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast, user]);

  const deleteKnowledgeBase = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('knowledge_bases')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting knowledge base:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete knowledge base',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast]);

  return {
    knowledgeBases,
    loading,
    fetchKnowledgeBases,
    getKnowledgeBase,
    getKnowledgeBaseByName,
    createKnowledgeBase,
    updateKnowledgeBase,
    deleteKnowledgeBase,
  };
}